import { egoCrisisChatTree } from './egoCrisisChatTree';

export interface ChatMessage {
  id: string;
  sender: 'tutor' | 'user';
  text: string;
  timestamp: string;
  avatar?: string;
  type?: 'text' | 'single-choice' | 'multiple-choice' | 'free-text' | 'dialogue-choice';
  options?: string[];
  correctAnswer?: string | string[];
  explanation?: string;
  /** Per-message avatar (RN `require(...)`) for multi-character JSON chats. */
  characterImage?: any;
  /** Per-message speaker name override. */
  characterName?: string;
}

/** Choix dans une discussion arborescente. */
export interface DialogueChoice {
  id: string;
  label: string;
  tutorResponse: string;
  /** false = reboucle sur le meme noeud apres la reaction du tuteur */
  advances: boolean;
  nextNodeId: string;
}

/** Noeud de discussion (tuteur + options utilisateur). */
export interface DialogueNode {
  id: string;
  tutorText: string;
  choices: DialogueChoice[];
  /** true = reponse evaluee (feedback rouge/vert). */
  isAssessment?: boolean;
  isEnd?: boolean;
}

export interface MissionConversationTree {
  startNodeId: string;
  nodes: Record<string, DialogueNode>;
}

export interface MissionChatData {
  missionId: number;
  missionTitle: string;
  tutorName: string;
  tutorAvatar: string;
  tutorColor?: string;
  tutorImage?: any;
  tutorRole: string;
  /** Script lineaire (quiz) — missions non migrees */
  messages?: ChatMessage[];
  /** Discussion a choix arborescente */
  conversationTree?: MissionConversationTree;
  unreadCount: number;
  lastActivity: string;
}

export function isTreeMission(data: MissionChatData): boolean {
  return data.conversationTree != null;
}

/** Nombre d'etapes de discussion (noeuds a choix, hors intro et outro). */
export function getTreeStepCount(tree: MissionConversationTree): number {
  return Object.values(tree.nodes).filter(
    n => !n.isEnd && n.choices.length > 0 && n.id !== tree.startNodeId,
  ).length;
}

export function getMissionChatScript(data: MissionChatData): ChatMessage[] {
  return data.messages ?? [];
}

export const mockChatData: MissionChatData[] = [
  {
    missionId: 1,
    missionTitle: 'EgoCrisis — AZ-104',
    tutorName: 'Nina Martin',
    tutorAvatar: 'NM',
    tutorColor: '#2D6A4F',
    tutorImage: require('../../assets/images/characters/egocrisis_nina.jpg'),
    tutorRole: 'Tech Lead — Azure Identity Architect',
    unreadCount: 1,
    lastActivity: '5m ago',
    conversationTree: egoCrisisChatTree as MissionConversationTree,
  },
  {
    missionId: 2,
    missionTitle: 'Foundation Gamble — AZ-900',
    tutorName: 'Lucas',
    tutorAvatar: 'LU',
    tutorColor: '#3B82F6',
    tutorImage: require('../../assets/images/characters/foundation_gamble_lucas_man_30s.jpg'),
    tutorRole: 'Cloud Architect',
    unreadCount: 1,
    lastActivity: '2m ago',
    messages: [
      {
        id: 'fg-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans Foundation Gamble. Je suis Lucas, Cloud Architect. On vient d'annoncer une migration Azure complete en 6 mois. Chaque decision d'architecture peut tout faire basculer. Pret a concevoir ?",
        timestamp: '09:00',
        type: 'text',
      },
      {
        id: 'fg-q1',
        sender: 'tutor',
        text: "Question 1 — Quel modele de service cloud permet aux developpeurs de deployer des applications sans gerer l'infrastructure sous-jacente ?",
        timestamp: '09:02',
        type: 'single-choice',
        options: ['IaaS', 'PaaS', 'SaaS', 'On-Premise'],
        correctAnswer: 'PaaS',
        explanation: "PaaS (Platform as a Service) gere l'infrastructure pour vous. Azure App Service en est un exemple parfait : vous deployez votre code, Azure s'occupe des serveurs, OS, et runtime.",
      },
      {
        id: 'fg-q2',
        sender: 'tutor',
        text: "Quels sont les avantages cles du cloud computing ? (Selectionnez toutes les bonnes reponses)",
        timestamp: '09:04',
        type: 'multiple-choice',
        options: [
          'Elasticite et scalabilite',
          'Couts fixes eleves',
          'Haute disponibilite',
          'Acces mondial via Internet',
          'Materiel proprietaire obligatoire',
        ],
        correctAnswer: ['Elasticite et scalabilite', 'Haute disponibilite', 'Acces mondial via Internet'],
        explanation: "Le cloud offre l'elasticite (scale up/down), la haute disponibilite (SLA 99,9%+) et un acces mondial. Il reduit les couts fixes et elimine le besoin de hardware proprietaire.",
      },
      {
        id: 'fg-outro',
        sender: 'tutor',
        text: "Solide. Tu maitrises les fondamentaux de l'architecture cloud. La migration Foundation Gamble peut commencer.",
        timestamp: '09:15',
        type: 'text',
      },
    ],
  },
  {
    missionId: 201,
    missionTitle: 'Architecture Under Fire — AZ-305',
    tutorName: 'Mina Brown',
    tutorAvatar: 'MB',
    tutorColor: '#2D6A4F',
    tutorImage: require('../../assets/images/characters/architecture_under_fire_Mina_Brown.jpg'),
    tutorRole: 'CTO — Hybrid Architecture Lead',
    unreadCount: 1,
    lastActivity: '3m ago',
    messages: [
      {
        id: 'auf-201-intro',
        sender: 'tutor',
        text: "Mina Brown ici. Focus: VPN site-to-site gb-lyon ↔ Azure West Europe. On passe en revue les points critiques avant la revue de 9h.",
        timestamp: '14:07',
        type: 'text',
      },
      {
        id: 'auf-201-q1',
        sender: 'tutor',
        text: 'Question 1 — Quelle région Azure est retenue pour la VPN Gateway dans ce scénario ?',
        timestamp: '14:11',
        type: 'single-choice',
        options: ['West Europe', 'North Europe', 'France Central', 'East US'],
        correctAnswer: 'West Europe',
        explanation: 'West Europe est imposée car le tier plateforme y est déjà déployé.',
      },
      {
        id: 'auf-201-q2',
        sender: 'tutor',
        text: "Question 2 — Quel type de gateway Mina impose pour préparer la phase 2 (BGP / DR) ?",
        timestamp: '14:11',
        type: 'single-choice',
        options: ['Policy-based', 'Route-based', 'Point-to-Site', 'ExpressRoute Gateway'],
        correctAnswer: 'Route-based',
        explanation: 'Route-based est non négociable pour BGP et les évolutions multi-tunnels.',
      },
      {
        id: 'auf-201-q2b',
        sender: 'tutor',
        text: 'Question 2b — Quelles informations sont indispensables dans le mode opératoire pour que Samir puisse câbler côté gb-lyon ? (plusieurs réponses)',
        timestamp: '14:11',
        type: 'multiple-choice',
        options: [
          'IP publique Azure de la VPN Gateway',
          'Plages CIDR on-prem annoncées via le tunnel',
          'Couleur du thème du portail Azure',
          'Type de gateway (route-based/policy-based)',
          'Nom du scrum board de la semaine',
        ],
        correctAnswer: [
          'IP publique Azure de la VPN Gateway',
          'Plages CIDR on-prem annoncées via le tunnel',
          'Type de gateway (route-based/policy-based)',
        ],
        explanation: "Pour câbler correctement, Samir a besoin des paramètres réseau et du type de tunnel, pas d'éléments UI/projet hors réseau.",
      },
      {
        id: 'auf-201-q3',
        sender: 'tutor',
        text: "Question 3 — Quelle phrase résume la contrainte de gouvernance de Mina pour la revue de 9h ?",
        timestamp: '14:12',
        type: 'single-choice',
        options: [
          "Déployer d'abord, documenter ensuite",
          'Livrer un mode opératoire défendable avant le déploiement',
          'Concentrer le tunnel sur le compute on-prem',
          'Reporter la validation après le câblage',
        ],
        correctAnswer: 'Livrer un mode opératoire défendable avant le déploiement',
        explanation: "Le livrable attendu est le document défendable, pas l'exécution technique immédiate.",
      },
      {
        id: 'auf-201-outro',
        sender: 'tutor',
        text: 'Parfait. On enchaîne ensuite sur les 4 briques: VNet, GatewaySubnet, VPN Gateway et Local Network Gateway.',
        timestamp: '14:13',
        type: 'text',
      },
    ],
  },
  {
    missionId: 102,
    missionTitle: 'Architecture Under Fire — AZ-305',
    tutorName: 'Mina Brown',
    tutorAvatar: 'MB',
    tutorColor: '#2D6A4F',
    tutorImage: require('../../assets/images/characters/architecture_under_fire_Mina_Brown.jpg'),
    tutorRole: 'CTO — Hybrid Architecture Lead',
    unreadCount: 1,
    lastActivity: '3m ago',
    messages: [
      {
        id: 'auf-102-intro',
        sender: 'tutor',
        text: "Mina Brown ici. Focus: VPN site-to-site gb-lyon ↔ Azure West Europe. On passe en revue les points critiques avant la revue de 9h.",
        timestamp: '14:07',
        type: 'text',
      },
      {
        id: 'auf-102-q1',
        sender: 'tutor',
        text: 'Question 1 — Quelle région Azure est retenue pour la VPN Gateway dans ce scénario ?',
        timestamp: '14:11',
        type: 'single-choice',
        options: ['West Europe', 'North Europe', 'France Central', 'East US'],
        correctAnswer: 'West Europe',
        explanation: 'West Europe est imposée car le tier plateforme y est déjà déployé.',
      },
      {
        id: 'auf-102-q2',
        sender: 'tutor',
        text: "Question 2 — Quel type de gateway Mina impose pour préparer la phase 2 (BGP / DR) ?",
        timestamp: '14:11',
        type: 'single-choice',
        options: ['Policy-based', 'Route-based', 'Point-to-Site', 'ExpressRoute Gateway'],
        correctAnswer: 'Route-based',
        explanation: 'Route-based est non négociable pour BGP et les évolutions multi-tunnels.',
      },
      {
        id: 'auf-102-q2b',
        sender: 'tutor',
        text: 'Question 2b — Quelles informations sont indispensables dans le mode opératoire pour que Samir puisse câbler côté gb-lyon ? (plusieurs réponses)',
        timestamp: '14:11',
        type: 'multiple-choice',
        options: [
          'IP publique Azure de la VPN Gateway',
          'Plages CIDR on-prem annoncées via le tunnel',
          'Couleur du thème du portail Azure',
          'Type de gateway (route-based/policy-based)',
          'Nom du scrum board de la semaine',
        ],
        correctAnswer: [
          'IP publique Azure de la VPN Gateway',
          'Plages CIDR on-prem annoncées via le tunnel',
          'Type de gateway (route-based/policy-based)',
        ],
        explanation: "Pour câbler correctement, Samir a besoin des paramètres réseau et du type de tunnel, pas d'éléments UI/projet hors réseau.",
      },
      {
        id: 'auf-102-q3',
        sender: 'tutor',
        text: "Question 3 — Quelle phrase résume la contrainte de gouvernance de Mina pour la revue de 9h ?",
        timestamp: '14:12',
        type: 'single-choice',
        options: [
          "Déployer d'abord, documenter ensuite",
          'Livrer un mode opératoire défendable avant le déploiement',
          'Concentrer le tunnel sur le compute on-prem',
          'Reporter la validation après le câblage',
        ],
        correctAnswer: 'Livrer un mode opératoire défendable avant le déploiement',
        explanation: "Le livrable attendu est le document défendable, pas l'exécution technique immédiate.",
      },
      {
        id: 'auf-102-outro',
        sender: 'tutor',
        text: 'Parfait. On enchaîne ensuite sur les 4 briques: VNet, GatewaySubnet, VPN Gateway et Local Network Gateway.',
        timestamp: '14:13',
        type: 'text',
      },
    ],
  },
  {
    missionId: 3,
    missionTitle: 'Data Storm — DP-203',
    tutorName: 'Aisha',
    tutorAvatar: 'AI',
    tutorColor: '#16A34A',
    tutorImage: require('../../assets/images/characters/data_storm_aisha_woman_30s.jpg'),
    tutorRole: 'Data Engineer Lead',
    unreadCount: 0,
    lastActivity: '15m ago',
    messages: [
      {
        id: 'ds-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans Data Storm. Je suis Aisha, Lead Data Engineer. On doit maitriser 42 millions de transactions par jour. La tempete de donnees n'attend pas. Pret ?",
        timestamp: '14:00',
        type: 'text',
      },
      {
        id: 'ds-q1',
        sender: 'tutor',
        text: "Question 1 — Quelle methode pandas utilise-t-on pour charger un fichier CSV dans un DataFrame ?",
        timestamp: '14:02',
        type: 'single-choice',
        options: ['pd.load_csv()', 'pd.read_csv()', 'pd.open_csv()', 'pd.import_csv()'],
        correctAnswer: 'pd.read_csv()',
        explanation: "pd.read_csv() est la methode standard. Elle accepte de nombreux parametres : encoding, sep, header, index_col, dtype, etc.",
      },
      {
        id: 'ds-outro',
        sender: 'tutor',
        text: "Excellent ! Tu maitrises les bases du traitement de donnees. Le pipeline Data Storm est entre de bonnes mains.",
        timestamp: '14:20',
        type: 'text',
      },
    ],
  },
  {
    missionId: 4,
    missionTitle: 'Cloud Heist — SAA-C03',
    tutorName: 'Nabil',
    tutorAvatar: 'NA',
    tutorColor: '#DC2626',
    tutorImage: require('../../assets/images/characters/cloud_heist_nabil_man_40s.jpg'),
    tutorRole: 'CISO',
    unreadCount: 0,
    lastActivity: '30m ago',
    messages: [
      {
        id: 'ch-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans Cloud Heist. Je suis Nabil, CISO. Keiko a ete mandatee pour penetrer nos defenses AWS avant les vrais attaquants. Tu dois securiser chaque acces. Prêt ?",
        timestamp: '10:00',
        type: 'text',
      },
      {
        id: 'ch-q1',
        sender: 'tutor',
        text: "Quelle politique IAM permet de limiter les permissions accordees a un role AWS ?",
        timestamp: '10:02',
        type: 'single-choice',
        options: ['Resource Policy', 'Permission Boundary', 'Service Control Policy', 'Trust Policy'],
        correctAnswer: 'Permission Boundary',
        explanation: "Une Permission Boundary definit le maximum de permissions qu'une identite peut avoir, meme si ses policies inline accordent plus. C'est un garde-fou essentiel contre l'escalade de privileges.",
      },
      {
        id: 'ch-outro',
        sender: 'tutor',
        text: "Bien. La surface d'attaque est reduite. Cloud Heist peut commencer.",
        timestamp: '10:15',
        type: 'text',
      },
    ],
  },
  {
    missionId: 5,
    missionTitle: 'Neural Override — AI-900',
    tutorName: 'Karim',
    tutorAvatar: 'KA',
    tutorColor: '#7C3AED',
    tutorImage: require('../../assets/images/characters/neural_override_karim_man_40s.jpg'),
    tutorRole: 'AI Lead',
    unreadCount: 0,
    lastActivity: '1h ago',
    messages: [
      {
        id: 'no-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans Neural Override. Je suis Karim, responsable IA. Les modeles de deep learning derivent en production. Tu dois reprendre le controle avant que l'IA ne surpasse ses concepteurs.",
        timestamp: '11:00',
        type: 'text',
      },
      {
        id: 'no-q1',
        sender: 'tutor',
        text: "Quel principe d'ethique IA garantit que les decisions algorithmiques peuvent etre expliquees et comprises par les humains ?",
        timestamp: '11:02',
        type: 'single-choice',
        options: ['Scalabilite', 'Explicabilite', 'Optimisation', 'Parallelisme'],
        correctAnswer: 'Explicabilite',
        explanation: "L'explicabilite (ou interpretabilite) est fondamentale : un systeme IA doit pouvoir justifier ses decisions de maniere comprehensible. Sans elle, la confiance et la gouvernance sont impossibles.",
      },
      {
        id: 'no-outro',
        sender: 'tutor',
        text: "Le systeme est sous controle. Neural Override peut progresser.",
        timestamp: '11:15',
        type: 'text',
      },
    ],
  },
  {
    missionId: 6,
    missionTitle: 'Pipeline Inferno — DevOps Pro',
    tutorName: 'Viktor',
    tutorAvatar: 'VI',
    tutorColor: '#EA580C',
    tutorImage: require('../../assets/images/characters/pipeline_inferno_viktor_man_30s.jpg'),
    tutorRole: 'DevOps Manager',
    unreadCount: 0,
    lastActivity: '45m ago',
    messages: [
      {
        id: 'pi-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans Pipeline Inferno. Je suis Viktor, DevOps Manager. Les pipelines critiques brulent. On a 4 heures avant le deploiement prod. Chaque minute compte.",
        timestamp: '08:00',
        type: 'text',
      },
      {
        id: 'pi-q1',
        sender: 'tutor',
        text: "Quel est l'objectif principal d'un pipeline CI/CD ?",
        timestamp: '08:02',
        type: 'single-choice',
        options: [
          'Remplacer les tests manuels',
          'Automatiser la validation et le deploiement du code',
          'Reduire le nombre de developpeurs',
          'Eliminer les environnements de test'
        ],
        correctAnswer: 'Automatiser la validation et le deploiement du code',
        explanation: "CI/CD automatise la chaine : integration du code, tests, build, et deploiement. Cela reduit les erreurs humaines et accelere la livraison tout en garantissant la qualite.",
      },
      {
        id: 'pi-outro',
        sender: 'tutor',
        text: "Pipeline stabilise. Le deploiement prod peut se faire en securite.",
        timestamp: '08:20',
        type: 'text',
      },
    ],
  },
  {
    missionId: 7,
    missionTitle: 'Merge Conflict — Git Pro',
    tutorName: 'Priya',
    tutorAvatar: 'PR',
    tutorColor: '#0891B2',
    tutorImage: require('../../assets/images/characters/merge_conflict_priya_woman_30s.jpg'),
    tutorRole: 'Tech Lead',
    unreadCount: 0,
    lastActivity: '20m ago',
    messages: [
      {
        id: 'mc-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans Merge Conflict. Je suis Priya, Tech Lead. Deux equipes dont les branches Git divergent depuis 3 mois. On doit resoudre le conflit technique ET humain.",
        timestamp: '15:00',
        type: 'text',
      },
      {
        id: 'mc-q1',
        sender: 'tutor',
        text: "Quelle strategie Git permet de maintenir un historique lineaire lors de l'integration d'une feature branch ?",
        timestamp: '15:02',
        type: 'single-choice',
        options: ['git merge --no-ff', 'git rebase', 'git cherry-pick', 'git reset --hard'],
        correctAnswer: 'git rebase',
        explanation: "git rebase rejoue les commits d'une branche sur une autre, creant un historique lineaire et propre. Contrairement au merge, il evite les commits de fusion et facilite la relecture de l'historique.",
      },
      {
        id: 'mc-outro',
        sender: 'tutor',
        text: "Conflit resolu. Les deux equipes peuvent continuer a travailler sur une base commune.",
        timestamp: '15:20',
        type: 'text',
      },
    ],
  },
  {
    missionId: 8,
    missionTitle: 'Deadline Paradox — PM Pro',
    tutorName: 'Sakura',
    tutorAvatar: 'SA',
    tutorColor: '#DB2777',
    tutorImage: require('../../assets/images/characters/deadline_paradox_sakura_woman_30s.jpg'),
    tutorRole: 'Project Manager',
    unreadCount: 0,
    lastActivity: '10m ago',
    messages: [
      {
        id: 'dp-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans Deadline Paradox. Je suis Sakura, Chef de Projet. La deadline est dans 5 jours pour 3 semaines de travail. On doit prioriser, negocier et livrer.",
        timestamp: '09:00',
        type: 'text',
      },
      {
        id: 'dp-q1',
        sender: 'tutor',
        text: "Selon la methode MoSCoW, quelle categorie represente les fonctionnalites importantes mais non critiques pour la livraison ?",
        timestamp: '09:02',
        type: 'single-choice',
        options: ['Must Have', 'Should Have', 'Could Have', 'Won\'t Have'],
        correctAnswer: 'Should Have',
        explanation: "Should Have regroupe les fonctionnalites importantes qui apportent une valeur significative mais dont l'absence ne compromet pas la livraison. Elles sont candidates a l'ajustement en cas de pression sur le planning.",
      },
      {
        id: 'dp-outro',
        sender: 'tutor',
        text: "Scope ajuste. Le paradoxe est resolu. La livraison est possible dans les delais.",
        timestamp: '09:20',
        type: 'text',
      },
    ],
  },

  {
    missionId: 105,
    missionTitle: 'The Learning Machine — Data Science',
    tutorName: 'Zara Chen',
    tutorAvatar: 'ZC',
    tutorColor: '#0F4C75',
    tutorImage: require('../../assets/images/characters/data_storm_jun_woman_30s.jpg'),
    tutorRole: 'ML Engineer',
    unreadCount: 1,
    lastActivity: '8m ago',
    messages: [
      {
        id: 'lm-intro-1',
        sender: 'tutor',
        text: "Bienvenue dans The Learning Machine. Je suis Zara Chen, ML Engineer. Notre algorithme vient d executer un trade de 200 millions sans validation humaine. On a deux heures pour comprendre pourquoi avant que la SEC n intervienne.",
        timestamp: '06:50',
        type: 'text',
      },
      {
        id: 'lm-q1',
        sender: 'tutor',
        text: "Le modele affiche 94% de precision sur le test set mais seulement 1% d ecart avec le training set. Quel est le premier signe d alerte ?",
        timestamp: '06:52',
        type: 'single-choice',
        options: [
          "Tout va bien — 94% est excellent et l ecart est minimal",
          "Une precision aussi elevee sur des donnees financieres est suspicieuse, verifier le data leakage en priorite",
          "Le modele underfits — ajouter plus de features",
          "L ecart de 1% indique de l overfitting severe",
        ],
        correctAnswer: "Une precision aussi elevee sur des donnees financieres est suspicieuse, verifier le data leakage en priorite",
        explanation: "En finance, une precision de 94% est anormalement elevee. Les marches financiers sont proches de l efficience — des modeles serieux atteignent rarement plus de 60-65%. Un ecart train/test minimal avec une precision tres haute pointe vers du data leakage, pas vers un modele exceptionnel.",
      },
      {
        id: 'lm-q2',
        sender: 'tutor',
        text: "Pour des donnees de trading temporelles, quelle methode de validation choisir ?",
        timestamp: '06:55',
        type: 'single-choice',
        options: [
          "K-Fold cross-validation classique",
          "TimeSeriesSplit — respecter l ordre chronologique est obligatoire",
          "Leave-one-out cross-validation",
          "Holdout split aleatoire 80/20",
        ],
        correctAnswer: "TimeSeriesSplit — respecter l ordre chronologique est obligatoire",
        explanation: "Un K-Fold aleatoire sur des series temporelles cree du leakage : le modele peut voir des donnees futures lors de la validation. TimeSeriesSplit garantit que l entrainement se fait toujours sur le passe et la validation sur le futur immediat. C est un prerequis non-negociable pour tout modele financier.",
      },
      {
        id: 'lm-q3',
        sender: 'tutor',
        text: "On decouvre que le StandardScaler a ete fit sur tout le dataset avant le split. Quel type de data leakage est-ce ?",
        timestamp: '06:58',
        type: 'single-choice',
        options: [
          "Target leakage — la cible est dans les features",
          "Train-test contamination — les stats du test set ont pollue le preprocessing",
          "Temporal leakage — utilisation de donnees futures",
          "Confirmation bias — le modele sur-echantillonne les gains",
        ],
        correctAnswer: "Train-test contamination — les stats du test set ont pollue le preprocessing",
        explanation: "Quand le StandardScaler est fite sur l ensemble du dataset (train + test), les statistiques de normalisation incorporent des informations sur le test set. Le modele beneficie donc d information qu il ne devrait pas avoir. La solution : fit_transform sur le training set uniquement, transform sur le test set.",
      },
      {
        id: 'lm-outro',
        sender: 'tutor',
        text: "Diagnostic complet. Data leakage par contamination de preprocessing confirme. La precision reelle du modele est de 61%, pas 94%. La position est coupee, le rapport SEC est prepare. The Learning Machine peut etre corrige.",
        timestamp: '07:15',
        type: 'text',
      },
    ],
  },
  {
    missionId: 106,
    missionTitle: 'The Ink Protocol — English Academic Writing',
    tutorName: 'Dr. Olga Smirnova',
    tutorAvatar: 'OS',
    tutorColor: '#4A1942',
    tutorImage: require('../../assets/images/characters/merge_conflict_olga_woman_40s.jpg'),
    tutorRole: 'Editor — Journal of Computational Pragmatics',
    unreadCount: 1,
    lastActivity: '12m ago',
    messages: [
      {
        id: 'ip-intro-1',
        sender: 'tutor',
        text: "Welcome to The Ink Protocol. I am Dr. Olga Smirnova, editor at the Journal of Computational Pragmatics. A plagiarism investigation is underway. Understanding citation rules is not optional — it is the foundation of academic credibility.",
        timestamp: '09:15',
        type: 'text',
      },
      {
        id: 'ip-q1',
        sender: 'tutor',
        text: "Prof. Novak paraphrased Kenji's thesis without citation. Under APA 7th edition, does a paraphrase require citation?",
        timestamp: '09:17',
        type: 'single-choice',
        options: [
          "No — only direct quotes require citation in APA",
          "Yes — paraphrases require author and year, even without page number",
          "Only if the paraphrase is longer than two sentences",
          "No — paraphrasing transforms the idea, so no citation is needed",
        ],
        correctAnswer: "Yes — paraphrases require author and year, even without page number",
        explanation: "APA 7th edition requires citation for both direct quotes and paraphrases. For paraphrases, author and year are mandatory; page number is recommended but not required. The obligation to cite is based on the origin of the idea, not the form in which it is expressed.",
      },
      {
        id: 'ip-q2',
        sender: 'tutor',
        text: "Which citation format is correct in APA 7th edition for a direct quote on page 23 by Chen (2022)?",
        timestamp: '09:20',
        type: 'single-choice',
        options: [
          "(Chen 23)",
          "(Chen, 2022, p. 23)",
          "[Chen, 2022, p. 23]",
          "Chen (2022: p.23)",
        ],
        correctAnswer: "(Chen, 2022, p. 23)",
        explanation: "APA 7th in-text citation for a direct quote: (Author, Year, p. Page). The comma separates each element. 'p.' precedes the page number. MLA uses (Author Page) without year and without comma. Never use square brackets for in-text APA citations — those are for editorial additions inside quotations.",
      },
      {
        id: 'ip-q3',
        sender: 'tutor',
        text: "What is the correct four-part structure for an APA-compliant abstract?",
        timestamp: '09:23',
        type: 'single-choice',
        options: [
          "Introduction, Hypothesis, Data, Conclusion",
          "Context, Method, Results, Conclusion",
          "Background, Literature Review, Findings, Implications",
          "Problem, Evidence, Analysis, Recommendation",
        ],
        correctAnswer: "Context, Method, Results, Conclusion",
        explanation: "An APA-compliant abstract follows a four-part structure: (1) Context — the research gap or problem; (2) Method — what was done and how; (3) Results — the key quantitative finding; (4) Conclusion — the implication or significance. This CMRC structure ensures the abstract is self-contained and readable independently of the full paper.",
      },
      {
        id: 'ip-outro',
        sender: 'tutor',
        text: "Well done. Prof. Novak's paper has been revised with correct APA citations. The Ethics Committee accepted the explanation. Academic integrity is not about perfection — it is about transparency, attribution, and correcting errors when they occur.",
        timestamp: '09:35',
        type: 'text',
      },
    ],
  },
];

export function getMissionChatData(missionId: number): MissionChatData | undefined {
  return mockChatData.find(data => data.missionId === missionId);
}
